import { Group, SegmentedControl, Stack, SegmentedControlItem } from '@mantine/core';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

type TabItem = {
  route: string;
  label: string;
  active?: boolean;  // Add the 'active' property
  stopNavigation?: boolean;
  loading?: boolean;
  /** Optional count pill on the tab label (e.g. ACTIVITY STREAM unread). 0/undefined = none. */
  badge?: number;
};

type Props = {
  tabs: Record<string, TabItem>;
  onTabChange?: (selectedTab: string) => void; // Make it optional
  loading?: boolean;
  value?: string;
};

export default function SegmentControlUpdated({ tabs, onTabChange, loading, value }: Props) {
  const navigate = useNavigate()
  const location = useLocation()

  const tabData: SegmentedControlItem[] = Object.entries(tabs).map(([, value]) => ({
    value: value.route,
    // With a badge, the label becomes a node: text + a pink count pill (prototype: min 20px
    // round pill, 11px/700 white on --lendos-pink). Without one, the plain string — identical
    // to what every existing consumer renders today.
    label:
      value.badge && value.badge > 0 ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {value.label}
          <span
            style={{
              minWidth: 20,
              height: 20,
              padding: '0 6px',
              borderRadius: 999,
              background: '#E8006E',
              color: '#FFFFFF',
              fontSize: 11,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {value.badge}
          </span>
        </span>
      ) : (
        value.label
      ),
  }))
  // Set the initial active tab based on the current location or the first tab
  const [activeTabRoute, setActiveTabRoute] = useState(() => {
    return (
      Object.values(tabs).find(tab => location.pathname === tab.route)?.route ||
      tabData[0]?.value ||
      ''
    );
  });

  // Only sync with URL if at least one tab is using navigation
  useEffect(() => {
    const hasRoutingTabs = Object.values(tabs).some(tab => !tab.stopNavigation);
    if (!hasRoutingTabs) return;

    const matchingTabRoute =
      Object.values(tabs).find(tab => location.pathname === tab.route)?.route ||
      tabData[0]?.value ||
      '';
    setActiveTabRoute(matchingTabRoute);
  }, [location.pathname, tabs, tabData]);

  function changeTab(e: string): void {
    setActiveTabRoute(e);

    if (onTabChange) {
      onTabChange(e); // Call only if onTabChange is provided
    }

    if (tabs[e]?.stopNavigation) return;

    navigate(e);
  }

  return (
    <Stack align="stretch" w="100%">
      <Group noWrap w="100%">
        <SegmentedControl
          disabled={loading}
          size="lg"
          onChange={changeTab}
          value={value ?? activeTabRoute}
          data={tabData}
          classNames={{
            root: 'segmented-root',
            label: 'segmented-label',
            indicator: 'segmented-indicator',
            controlActive: 'segmented-control-active',
            control: 'segmented-control',
          }}
        />

      </Group>
    </Stack>
  );
}
